import { useState } from 'react';
import type { Material } from '@lms/shared/types';
import { AlertCircle, Download, ExternalLink, FileText, Headphones, Video } from 'lucide-react';
import { normalizeMediaSource, resolveMediaUrl } from '@lms/shared/utils';
import { useLanguage } from '../lib/LanguageContext';
import { apiBaseUrl } from '../lib/api-client';
import { copy } from '../copy/copy';

interface MediaPlayerProps {
  material: Material;
}

type MediaTab = 'VIDEO' | 'AUDIO' | 'PDF';

function isMediaReference(value?: string | null): boolean {
  return Boolean(
    value &&
    (/^(https?:\/\/|\/|blob:|data:(?:video\/|audio\/|application\/pdf))/i.test(value.trim()) ||
      /^[\w.-]+\/[\w./%-]+(?:[?#].*)?$/.test(value.trim()) ||
      /\.(mp4|webm|mov|mp3|m4a|ogg|wav|pdf)(?:[?#].*)?$/i.test(value.trim()))
  );
}

function isAudioFile(value: string): boolean {
  try {
    return /\.(?:mp3|m4a|aac|wav|ogg|flac)(?:$|[?#])/i.test(new URL(value).pathname);
  } catch {
    return /\.(?:mp3|m4a|aac|wav|ogg|flac)(?:$|[?#])/i.test(value);
  }
}

function isVideoFile(value: string): boolean {
  return /\.(?:mp4|webm|m4v|mov)(?:$|[?#])/i.test(value);
}

function getYouTubeEmbed(url: string): string | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    const id =
      host === 'youtu.be' || host === 'www.youtu.be'
        ? parsed.pathname.split('/')[1]
        : ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'www.youtube-nocookie.com'].includes(
              host
            )
          ? parsed.searchParams.get('v') ||
            parsed.pathname.match(/^\/(?:embed|shorts|live)\/([^/]+)/)?.[1]
          : null;
    return id && /^[\w-]{11}$/.test(id)
      ? `https://www.youtube-nocookie.com/embed/${id}?rel=0`
      : null;
  } catch {
    return null;
  }
}

function getDrivePreview(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== 'drive.google.com') return null;
    const fileId =
      parsed.pathname.match(/^\/file\/d\/([^/]+)/)?.[1] ||
      (['/open', '/uc'].includes(parsed.pathname) ? parsed.searchParams.get('id') : null);
    return fileId && /^[\w-]+$/.test(fileId)
      ? `https://drive.google.com/file/d/${encodeURIComponent(fileId)}/preview`
      : null;
  } catch {
    return null;
  }
}

export function MediaPlayer({ material }: MediaPlayerProps) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<MediaTab | null>(null);
  const [hasError, setHasError] = useState(false);
  const [videoSourceIndex, setVideoSourceIndex] = useState(0);
  const media = [...(material.media || [])].sort((a, b) => a.order - b.order);
  const normalizedContent = normalizeMediaSource(material.content);
  const contentUrl = isMediaReference(normalizedContent) ? normalizedContent : undefined;
  const embeddedContentUrl = material.content
    ?.match(/https?:\/{1,2}[^\s<>]+/gi)
    ?.map(url => normalizeMediaSource(url))
    ?.filter((url): url is string => Boolean(url))
    ?.find(url => getYouTubeEmbed(url) || getDrivePreview(url));
  const embeddedDescriptionUrl = material.description
    ?.match(/https?:\/{1,2}[^\s<>]+/gi)
    ?.map(url => normalizeMediaSource(url))
    ?.filter((url): url is string => Boolean(url))
    ?.find(url => getYouTubeEmbed(url) || getDrivePreview(url));
  const primaryUrl = normalizeMediaSource(material.mediaUrl);

  const videoSources = [
    material.type === 'VIDEO' ? embeddedContentUrl : undefined,
    material.type === 'VIDEO' ? embeddedDescriptionUrl : undefined,
    material.type === 'VIDEO' ? contentUrl : undefined,
    material.type !== 'AUDIO' && material.type !== 'DISCUSSION' ? primaryUrl : undefined,
    ...media
      .filter(item => {
        if (item.type === 'VIDEO') return true;
        const url = normalizeMediaSource(item.url) || '';
        return (
          item.type === 'LINK' &&
          (isVideoFile(url) || Boolean(getYouTubeEmbed(url) || getDrivePreview(url)))
        );
      })
      .map(item => item.url),
  ]
    .flatMap(url => {
      if (!url) return [];
      const resolved = resolveMediaUrl(url, apiBaseUrl);
      return resolved && resolved !== url && /^https?:\/\//i.test(url)
        ? [resolved, url]
        : resolved
          ? [resolved]
          : [];
    })
    .filter((url): url is string => typeof url === 'string' && !isAudioFile(url));
  // Shared links must be embedded; a stale native media URL may also be present.
  const embeddableSources = videoSources.filter(
    url => getYouTubeEmbed(url) || getDrivePreview(url)
  );
  const prioritizedVideoSources = [...new Set([...embeddableSources, ...videoSources])];
  const videoUrl = prioritizedVideoSources[videoSourceIndex];
  const audioUrl = resolveMediaUrl(
    ((material.type === 'AUDIO' || material.type === 'DISCUSSION') && (primaryUrl || contentUrl)) ||
      media.find(item => item.type === 'AUDIO')?.url ||
      [primaryUrl, contentUrl, ...media.map(item => item.url)].find(
        url => typeof url === 'string' && isAudioFile(url)
      ),
    apiBaseUrl
  );
  const pdfUrl = resolveMediaUrl(
    material.pdfUrl ||
      (material.type === 'DOCUMENT' ? primaryUrl || contentUrl : undefined) ||
      media.find(item => item.type === 'PDF')?.url,
    apiBaseUrl
  );

  const tabs: MediaTab[] = [
    ...(videoUrl ? ['VIDEO' as const] : []),
    ...(audioUrl ? ['AUDIO' as const] : []),
    ...(pdfUrl ? ['PDF' as const] : []),
  ];
  const preferredTab: MediaTab =
    material.type === 'DOCUMENT'
      ? 'PDF'
      : material.type === 'AUDIO' || material.type === 'DISCUSSION'
        ? 'AUDIO'
        : 'VIDEO';
  const selectedTab =
    activeTab && tabs.includes(activeTab)
      ? activeTab
      : tabs.includes(preferredTab)
        ? preferredTab
        : tabs[0];
  const sourceUrl =
    selectedTab === 'VIDEO' ? videoUrl : selectedTab === 'AUDIO' ? audioUrl : pdfUrl;
  const tabLabels = {
    VIDEO: copy.mediaPlayer.videoLabel,
    AUDIO: copy.mediaPlayer.audioLabel,
    PDF: copy.mediaPlayer.pdfLabel,
  };
  const tabIcons = { VIDEO: Video, AUDIO: Headphones, PDF: FileText };

  const selectTab = (tab: MediaTab) => {
    setActiveTab(tab);
    setHasError(false);
  };

  const handleVideoError = () => {
    if (videoSourceIndex + 1 < prioritizedVideoSources.length) {
      setVideoSourceIndex(index => index + 1);
    } else {
      setHasError(true);
    }
  };

  if (!sourceUrl || !selectedTab) {
    const isInteractive = material.type === 'QUIZ' || material.type === 'REFLECTION';
    return (
      <div className="flex min-h-52 flex-col items-center justify-center gap-3 bg-surface-container-low p-6 text-center sm:min-h-64">
        <AlertCircle size={32} className="text-primary" aria-hidden="true" />
        <h2 className="m-0 font-headline text-lg font-bold text-on-surface">
          {t(isInteractive ? copy.mediaPlayer.interactiveMaterial : copy.mediaPlayer.noMedia)}
        </h2>
        <p className="m-0 max-w-md text-sm leading-relaxed text-on-surface-variant">
          {t(
            isInteractive ? copy.mediaPlayer.interactiveMaterialDesc : copy.mediaPlayer.noMediaDesc
          )}
        </p>
      </div>
    );
  }

  const youtubeEmbed = selectedTab === 'VIDEO' ? getYouTubeEmbed(sourceUrl) : null;
  const drivePreview =
    selectedTab === 'VIDEO' || selectedTab === 'PDF' ? getDrivePreview(sourceUrl) : null;

  return (
    <section
      aria-label={t(copy.mediaPlayer.mediaChoices)}
      className="min-w-0 bg-surface-container-lowest"
    >
      {tabs.length > 1 && (
        <div
          role="tablist"
          aria-label={t(copy.mediaPlayer.mediaChoices)}
          className="flex gap-2 overflow-x-auto bg-surface-container-low p-3 sm:p-4"
        >
          {tabs.map(tab => {
            const Icon = tabIcons[tab];
            return (
              <button
                key={tab}
                type="button"
                role="tab"
                id={`media-tab-${tab.toLowerCase()}`}
                aria-selected={selectedTab === tab}
                aria-controls="material-media-panel"
                tabIndex={selectedTab === tab ? 0 : -1}
                onClick={() => selectTab(tab)}
                onKeyDown={event => {
                  const current = tabs.indexOf(tab);
                  const nextIndex =
                    event.key === 'ArrowRight'
                      ? (current + 1) % tabs.length
                      : event.key === 'ArrowLeft'
                        ? (current - 1 + tabs.length) % tabs.length
                        : event.key === 'Home'
                          ? 0
                          : event.key === 'End'
                            ? tabs.length - 1
                            : null;
                  if (nextIndex === null) return;
                  event.preventDefault();
                  const nextTab = tabs[nextIndex];
                  selectTab(nextTab);
                  document.getElementById(`media-tab-${nextTab.toLowerCase()}`)?.focus();
                }}
                className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95 ${selectedTab === tab ? 'bg-primary text-on-primary shadow-level1' : 'bg-surface-container-lowest text-on-surface-variant hover:bg-surface-container-high'}`}
              >
                <Icon size={17} aria-hidden="true" />
                {t(tabLabels[tab])}
              </button>
            );
          })}
        </div>
      )}

      <div
        id="material-media-panel"
        role={tabs.length > 1 ? 'tabpanel' : undefined}
        aria-labelledby={tabs.length > 1 ? `media-tab-${selectedTab.toLowerCase()}` : undefined}
      >
        {selectedTab === 'VIDEO' && (
          <div className="relative aspect-video w-full overflow-hidden bg-on-surface">
            {hasError ? (
              <div
                role="alert"
                className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-surface-container-low p-5 text-center text-on-surface"
              >
                <AlertCircle size={32} className="text-error" aria-hidden="true" />
                <p className="m-0 font-bold">{t(copy.mediaPlayer.loadFailed)}</p>
                <p className="m-0 text-sm">{t(copy.mediaPlayer.loadFailedDesc)}</p>
                <button
                  type="button"
                  onClick={() => {
                    setVideoSourceIndex(0);
                    setHasError(false);
                  }}
                  className="min-h-11 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-on-primary focus-visible:outline-2 focus-visible:outline-primary active:scale-95"
                >
                  {t(copy.mediaPlayer.retryVideo)}
                </button>
              </div>
            ) : youtubeEmbed || drivePreview ? (
              <iframe
                title={material.title}
                className="absolute inset-0 size-full"
                src={youtubeEmbed || drivePreview || ''}
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                onError={handleVideoError}
              />
            ) : (
              <video
                controls
                playsInline
                preload="metadata"
                aria-label={material.title}
                className="size-full object-contain"
                src={sourceUrl}
                onError={handleVideoError}
              >
                {t(copy.mediaPlayer.browserNotSupportVideo)}
              </video>
            )}
          </div>
        )}

        {selectedTab === 'AUDIO' && (
          <div className="flex flex-col items-center gap-5 bg-gradient-to-br from-primary/10 via-surface-container-lowest to-secondary-container/40 px-5 py-9 text-center sm:py-12">
            <div className="flex size-20 items-center justify-center rounded-3xl bg-primary text-on-primary shadow-level2">
              <Headphones size={34} aria-hidden="true" />
            </div>
            <div className="max-w-lg space-y-2">
              <h2 className="m-0 font-headline text-xl font-bold text-on-surface">
                {t(copy.mediaPlayer.audioResource)}
              </h2>
              <p className="m-0 text-sm leading-relaxed text-on-surface-variant">
                {t(copy.mediaPlayer.audioResourceDesc)}
              </p>
            </div>
            <audio
              controls
              preload="metadata"
              aria-label={material.title}
              className="min-h-12 w-full max-w-xl"
              src={sourceUrl}
              onError={() => setHasError(true)}
            >
              {t(copy.mediaPlayer.browserNotSupportAudio)}
            </audio>
            {hasError && (
              <p role="alert" className="m-0 text-sm font-medium text-error">
                {t(copy.mediaPlayer.audioLoadFailed)}
              </p>
            )}
          </div>
        )}

        {selectedTab === 'PDF' && (
          <div className="bg-surface-container-low">
            <div className="flex flex-wrap items-center justify-between gap-3 bg-surface-container p-4 sm:p-5">
              <div className="flex min-w-0 items-center gap-3">
                <FileText size={22} className="shrink-0 text-primary" aria-hidden="true" />
                <div className="min-w-0">
                  <h2 className="m-0 font-headline text-base font-bold text-on-surface">
                    {t(copy.mediaPlayer.docResource)}
                  </h2>
                  <p className="m-0 text-xs text-on-surface-variant">
                    {t(copy.mediaPlayer.docResourceDesc)}
                  </p>
                </div>
              </div>
              <div className="flex w-full flex-wrap gap-2 sm:w-auto">
                <a
                  href={sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-surface-container-lowest px-3 py-2 text-xs font-bold text-primary no-underline hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-primary active:scale-95 sm:flex-none"
                >
                  <ExternalLink size={16} aria-hidden="true" />
                  {t(copy.mediaPlayer.openNewTab)}
                </a>
                <a
                  href={sourceUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-primary to-primary-container px-3 py-2 text-xs font-bold text-on-primary no-underline focus-visible:outline-2 focus-visible:outline-primary active:scale-95 sm:flex-none"
                >
                  <Download size={16} aria-hidden="true" />
                  {t(copy.mediaPlayer.download)}
                </a>
              </div>
            </div>
            {hasError ? (
              <p role="alert" className="m-0 p-6 text-center text-sm text-on-surface-variant">
                {t(copy.mediaPlayer.previewNotAvailableDesc)}
              </p>
            ) : (
              <iframe
                title={material.title}
                src={drivePreview || sourceUrl}
                loading="lazy"
                onError={() => setHasError(true)}
                className="h-[min(70svh,52rem)] min-h-96 w-full bg-surface-container-low lg:h-[min(78vh,64rem)]"
              />
            )}
          </div>
        )}
      </div>

      {(selectedTab === 'VIDEO' || selectedTab === 'AUDIO') && (
        <div className="flex flex-wrap items-center justify-end gap-2 bg-surface-container-lowest px-4 py-3">
          {selectedTab === 'VIDEO' && prioritizedVideoSources.length > 1 && (
            <button
              type="button"
              onClick={() => {
                setVideoSourceIndex(index => (index + 1) % prioritizedVideoSources.length);
                setHasError(false);
              }}
              className="min-h-11 rounded-lg bg-surface-container-low px-3 py-2 text-xs font-bold text-primary transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-primary active:scale-95"
            >
              {t(copy.mediaPlayer.nextVideoSource)}
            </button>
          )}
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-11 items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold text-primary no-underline transition-colors hover:bg-surface-container-low focus-visible:outline-2 focus-visible:outline-primary active:scale-95"
          >
            <ExternalLink size={16} aria-hidden="true" />
            {t(copy.mediaPlayer.openNewTab)}
          </a>
        </div>
      )}
    </section>
  );
}
